<template>
  <v-container fluid class="pa-0 ma-0">
    <!-- Header -->
    <v-card class="mb-4 card-section" elevation="3">
      <v-card-title class="section-title py-3 d-flex align-center">
        <v-icon left color="#fff" size="22">mdi-calculator-variant</v-icon>
        <span class="font-weight-bold text-h6" style="color: #fff;">Calculadora do Piso Mínimo de Frete (ANTT)</span>
        <v-spacer></v-spacer>
        <v-chip outlined dark small style="border-color:#fff; color:#fff;">
          Resolução 6.076/2026
        </v-chip>
      </v-card-title>
    </v-card>

    <v-alert type="info" dense text class="mb-4" color="#1d4ed8">
      Ferramenta apenas para consulta. O valor não é enviado para o cadastro de CIOT ou MDF-e.
      Coeficientes baseados na Resolução ANTT nº 6.076/2026 (vigência 20/01/2026).
    </v-alert>

    <v-row>
      <!-- Formulário -->
      <v-col cols="12" md="6">
        <v-card class="card-section pa-4" elevation="2">
          <div class="step-card-header mb-3">
            <v-icon color="#1d4ed8" class="mr-2">mdi-tune</v-icon>
            <span class="step-card-title">Parâmetros</span>
          </div>

          <v-row dense>
            <v-col cols="12">
              <v-select
                v-model="form.tabela"
                :items="tabelas"
                item-text="text"
                item-value="value"
                label="Tabela / Tipo de Contratação *"
                outlined dense hide-details="auto"
                prepend-inner-icon="mdi-table"
              ></v-select>
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="form.tipoCarga"
                :items="tiposCarga"
                item-text="text"
                item-value="value"
                label="Tipo de Carga *"
                outlined dense hide-details="auto"
                prepend-inner-icon="mdi-package-variant"
              ></v-select>
            </v-col>

            <v-col cols="12" sm="6">
              <v-select
                v-model="form.eixos"
                :items="eixosDisponiveis"
                label="Nº de Eixos *"
                outlined dense hide-details="auto"
                prepend-inner-icon="mdi-axis-arrow"
              ></v-select>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.distanciaKm"
                label="Distância (km) *"
                type="number"
                min="0" step="1"
                outlined dense hide-details="auto"
                prepend-inner-icon="mdi-map-marker-distance"
                suffix="km"
              ></v-text-field>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="form.valorPretendido"
                label="Valor pretendido (opcional)"
                type="number"
                min="0" step="0.01"
                outlined dense hide-details="auto"
                prepend-inner-icon="mdi-currency-brl"
                prefix="R$"
                hint="Informe para comparar com o piso mínimo"
                persistent-hint
              ></v-text-field>
            </v-col>
          </v-row>

          <div class="d-flex justify-end mt-4">
            <v-btn text color="grey darken-1" @click="limpar" class="mr-2">
              <v-icon left small>mdi-refresh</v-icon>
              Limpar
            </v-btn>
            <v-btn color="#1d4ed8" dark elevation="2" @click="calcular">
              <v-icon left>mdi-calculator</v-icon>
              Calcular
            </v-btn>
          </div>
        </v-card>
      </v-col>

      <!-- Resultado -->
      <v-col cols="12" md="6">
        <v-card class="card-section pa-4" elevation="2">
          <div class="step-card-header mb-3">
            <v-icon color="#1d4ed8" class="mr-2">mdi-chart-box</v-icon>
            <span class="step-card-title">Resultado</span>
          </div>

          <v-alert v-if="resultado && resultado.erro" type="warning" dense text>
            {{ resultado.erro }}
          </v-alert>

          <div v-else-if="resultado" class="resultado-box">
            <div class="piso-destaque">
              <div class="piso-label">Piso Mínimo de Frete</div>
              <div class="piso-valor">R$ {{ formatMoney(resultado.piso) }}</div>
            </div>

            <v-divider class="my-3"></v-divider>

            <div class="resumo-section">
              <div class="resumo-item"><b>Fórmula:</b> (CCD × distância) + CC</div>
              <div class="resumo-item">
                <b>Cálculo:</b>
                (R$ {{ formatNum(resultado.ccd, 4) }} × {{ resultado.distanciaKm }} km) + R$ {{ formatMoney(resultado.cc) }}
              </div>
              <div class="resumo-item"><b>CCD (Coef. Deslocamento):</b> R$ {{ formatNum(resultado.ccd, 4) }} / km</div>
              <div class="resumo-item"><b>CC (Coef. Carga/Descarga):</b> R$ {{ formatMoney(resultado.cc) }}</div>
              <div class="resumo-item"><b>Valor médio por km:</b> R$ {{ formatMoney(resultado.valorPorKm) }} / km</div>
            </div>

            <template v-if="comparacao">
              <v-divider class="my-3"></v-divider>
              <v-alert
                :type="comparacao.acimaDoPiso ? 'success' : 'error'"
                dense text class="mb-0"
              >
                <div class="font-weight-bold">
                  Valor pretendido: R$ {{ formatMoney(comparacao.valorPretendido) }}
                </div>
                <div>
                  {{ comparacao.acimaDoPiso ? 'Acima' : 'Abaixo' }} do piso em
                  <b>R$ {{ formatMoney(Math.abs(comparacao.diferenca)) }}</b>
                  ({{ formatNum(Math.abs(comparacao.percentual), 2) }}%)
                </div>
                <div v-if="!comparacao.acimaDoPiso" class="mt-1" style="font-size: 0.85rem;">
                  ⚠️ A ANTT bloqueia a geração do CIOT quando o frete está abaixo do piso.
                </div>
              </v-alert>
            </template>
          </div>

          <div v-else class="text--secondary text-center py-6">
            <v-icon size="42" color="grey lighten-1">mdi-calculator</v-icon>
            <div class="mt-2">Preencha os parâmetros e clique em Calcular</div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="card-section mt-4 pa-4" elevation="1">
      <div class="text-caption text--secondary">
        <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
        <b>Sobre as tabelas:</b>
        <b>A</b> — composição completa, operação padrão (Lotação) ·
        <b>B</b> — somente unidade tratora, operação padrão ·
        <b>C</b> — composição completa, Alto Desempenho (carga + descarga ≤ 3h) ·
        <b>D</b> — somente unidade tratora, Alto Desempenho.
        Reajustes anuais publicados em janeiro pela ANTT.
      </div>
    </v-card>
  </v-container>
</template>

<script>
import {
  TIPOS_CARGA,
  EIXOS_DISPONIVEIS,
  TABELAS_INFO,
  calcularPisoFrete,
  compararValor
} from '../utils/pisoFreteAntt'

export default {
  name: 'CalculadoraPisoFrete',
  data(){
    return {
      tabelas: TABELAS_INFO,
      tiposCarga: TIPOS_CARGA,
      eixosDisponiveis: EIXOS_DISPONIVEIS,
      form: {
        tabela: 'A',
        tipoCarga: 'CARGA_GERAL',
        eixos: 4,
        distanciaKm: '',
        valorPretendido: ''
      },
      resultado: null,
      comparacao: null
    }
  },
  methods: {
    calcular(){
      this.resultado = calcularPisoFrete({
        tabela: this.form.tabela,
        tipoCarga: this.form.tipoCarga,
        eixos: this.form.eixos,
        distanciaKm: this.form.distanciaKm
      })
      if(this.resultado && !this.resultado.erro){
        this.comparacao = compararValor(this.resultado.piso, this.form.valorPretendido)
      } else {
        this.comparacao = null
      }
    },
    limpar(){
      this.form = {
        tabela: 'A',
        tipoCarga: 'CARGA_GERAL',
        eixos: 4,
        distanciaKm: '',
        valorPretendido: ''
      }
      this.resultado = null
      this.comparacao = null
    },
    formatMoney(v){
      if(v === null || v === undefined || isNaN(v)) return '0,00'
      return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatNum(v, dec){
      if(v === null || v === undefined || isNaN(v)) return '0'
      return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec })
    }
  }
}
</script>

<style scoped>
.card-section {
  border-radius: 14px !important;
  background: linear-gradient(145deg, #f9fbff 0%, #ffffff 60%, #eef2ff 100%);
  box-shadow: 0 14px 36px rgba(29, 78, 216, 0.08);
}

.section-title {
  background: #1d4ed8;
  color: #fff !important;
  border-radius: 10px 10px 0 0;
  padding: 8px 16px !important;
}

.step-card-header {
  display: flex;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 2px solid #eef2ff;
}

.step-card-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
}

.resultado-box {
  padding: 4px;
}

.piso-destaque {
  background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%);
  border-radius: 12px;
  padding: 18px;
  text-align: center;
  color: #fff;
  box-shadow: 0 6px 16px rgba(29, 78, 216, 0.25);
}

.piso-label {
  font-size: 0.85rem;
  opacity: 0.85;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.piso-valor {
  font-size: 2rem;
  font-weight: 700;
  margin-top: 4px;
}

.resumo-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
}

.resumo-item {
  font-size: 0.9rem;
  color: #334155;
  padding: 3px 0;
}
</style>
